import React, { useState, useEffect, useCallback } from 'react';
import { ScheduleRoomMapping } from './ScheduleRoomMapping';

export type ScheduleType = string;
export type ExtractionStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface Schedule {
  id: number;
  project_id: number;
  document_id: number;
  schedule_type: ScheduleType;
  custom_name: string | null;
  extraction_mode: string;
  header_row: number;
  room_column: number;
  extraction_status: ExtractionStatus;
  extraction_error: string | null;
  version_count: number;
  current_version: {
    version_number: number;
    total_rows: number;
    matched_count: number;
    unmatched_count: number;
    extracted_data: Array<Record<string, string | number>> | null;
    created_at: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleChange {
  id: number;
  change_type: 'added' | 'removed' | 'modified';
  room_number: string;
  change_summary: string;
  previous_data: any;
  current_data: any;
  is_acknowledged: boolean;
  created_at: string;
}

export interface ScheduleChangesSummary {
  schedule_id: number;
  version_from: number;
  version_to: number;
  total_changes: number;
  added: number;
  removed: number;
  modified: number;
  changes: ScheduleChange[];
}

interface Props {
  projectId: number;
  roomId?: number;
  token: string;
  refreshTrigger?: number;
}

type TabType = 'schedules' | 'preview' | 'mappings' | 'changes';

const STATUS_CONFIG: Record<ExtractionStatus, { icon: string; color: string; message: string }> = {
  pending: { icon: '⏳', color: '#666', message: 'Waiting to process' },
  processing: { icon: '🔄', color: '#007bff', message: 'Extracting tables...' },
  ready: { icon: '✓', color: '#28a745', message: 'Ready' },
  failed: { icon: '✗', color: '#dc3545', message: 'Failed' },
};

function ScheduleErrorBoundary({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{
      padding: '40px',
      textAlign: 'center',
      background: '#fee',
      borderRadius: '8px',
      border: '1px solid #fcc',
    }}>
      <p style={{ color: '#c00', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
        Schedule Viewer Error
      </p>
      <p style={{ color: '#666', marginBottom: '16px' }}>{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '8px 16px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

export function ScheduleViewer({ projectId, roomId, token, refreshTrigger }: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('schedules');
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [changesSummary, setChangesSummary] = useState<ScheduleChangesSummary | null>(null);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [pollingScheduleId, setPollingScheduleId] = useState<number | null>(null);
  const [versionDetail, setVersionDetail] = useState<{ matched_rooms: any } | null>(null);
  const [editRoomColumn, setEditRoomColumn] = useState<number | null>(null);
  const [savingColumn, setSavingColumn] = useState(false);
  const [deleteModalSchedule, setDeleteModalSchedule] = useState<Schedule | null>(null);

  interface DeleteConfirmationModalProps {
    schedule: Schedule;
    onDeleteAll: () => void;
    onDeleteLatest: () => void;
    onCancel: () => void;
  }

  function DeleteConfirmationModal({ schedule, onDeleteAll, onDeleteLatest, onCancel }: DeleteConfirmationModalProps) {
    const hasMultipleVersions = schedule.version_count >= 2;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Delete Schedule</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            <strong>{schedule.schedule_type}</strong> has {schedule.version_count} version{schedule.version_count !== 1 ? 's' : ''}.
            How would you like to delete?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {hasMultipleVersions && (
              <button
                onClick={onDeleteLatest}
                style={{
                  padding: '12px',
                  background: '#f0ad4e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                Delete Latest Version (v{schedule.version_count}) Only
              </button>
            )}
            <button
              onClick={onDeleteAll}
              style={{
                padding: '12px',
                background: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              Delete All Versions
            </button>
            <button
              onClick={onCancel}
              style={{
                padding: '10px',
                background: '#f0f0f0',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  const loadSchedules = useCallback(async () => {
    try {
      const response = await fetch(`/api/projects/${projectId}/schedules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load schedules');
      const data = await response.json();
      setSchedules(data);

      // Start polling for any processing schedules
      const processingSchedule = data.find((s: Schedule) => s.extraction_status === 'processing');
      if (processingSchedule && !pollingScheduleId) {
        setPollingScheduleId(processingSchedule.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [projectId, token, pollingScheduleId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadSchedules();
    }
  }, [refreshTrigger]); // eslint-disable-line

  // Poll for status updates on processing schedules
  useEffect(() => {
    if (!pollingScheduleId) return;

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/schedules/${pollingScheduleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();

        if (data.extraction_status !== 'processing') {
          setPollingScheduleId(null);
          loadSchedules();
          if (data.extraction_status === 'ready' && selectedSchedule?.id === data.id) {
            setSelectedSchedule(data);
          }
        }
      } catch {
        // Silently fail polling errors
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [pollingScheduleId, token, loadSchedules, selectedSchedule]);

  // Fetch version detail for diagnostics when entering preview
  useEffect(() => {
    if (activeTab !== 'preview' || !selectedSchedule) return;
    setVersionDetail(null);
    setEditRoomColumn(selectedSchedule.room_column);
    fetch(`/api/schedules/${selectedSchedule.id}/versions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(versions => {
        if (versions && versions.length > 0) {
          setVersionDetail(versions[0]);
        }
      })
      .catch(() => {});
  }, [activeTab, selectedSchedule?.id, token]);

  const handleSaveRoomColumn = async () => {
    if (!selectedSchedule || editRoomColumn === null) return;
    setSavingColumn(true);
    try {
      await fetch(`/api/schedules/${selectedSchedule.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_column: editRoomColumn }),
      });
      await fetch(`/api/schedules/${selectedSchedule.id}/extract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setPollingScheduleId(selectedSchedule.id);
      setActiveTab('schedules');
      loadSchedules();
    } finally {
      setSavingColumn(false);
    }
  };

  const handleDelete = async (scheduleId: number) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;
    setDeleteModalSchedule(schedule);
  };

  const handleDeleteAllVersions = async () => {
    if (!deleteModalSchedule) return;
    try {
      const response = await fetch(`/api/schedules/${deleteModalSchedule.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete');
      setDeleteModalSchedule(null);
      loadSchedules();
      if (selectedSchedule?.id === deleteModalSchedule.id) {
        setSelectedSchedule(null);
        setActiveTab('schedules');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
      setDeleteModalSchedule(null);
    }
  };

  const handleDeleteLatestVersion = async () => {
    if (!deleteModalSchedule) return;
    try {
      const response = await fetch(`/api/schedules/${deleteModalSchedule.id}/versions/latest`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to delete version');
      }
      setDeleteModalSchedule(null);
      loadSchedules();
      // If viewing this schedule, refresh it to show the rolled-back version
      if (selectedSchedule?.id === deleteModalSchedule.id) {
        const updated = schedules.find(s => s.id === deleteModalSchedule.id);
        if (updated) {
          setSelectedSchedule({ ...updated, version_count: updated.version_count - 1 });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete version');
      setDeleteModalSchedule(null);
    }
  };

  const handleExtract = async (scheduleId: number) => {
    try {
      const response = await fetch(`/api/schedules/${scheduleId}/extract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to trigger extraction');
      setPollingScheduleId(scheduleId);
      loadSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger extraction');
    }
  };

  const handleViewChanges = async (schedule: Schedule) => {
    if (!schedule.current_version || schedule.version_count <= 1) return;
    setLoadingChanges(true);
    setSelectedSchedule(schedule);
    try {
      const response = await fetch(`/api/schedules/${schedule.id}/changes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load changes');
      const data = await response.json();
      setChangesSummary(data);
      setActiveTab('changes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load changes');
    } finally {
      setLoadingChanges(false);
    }
  };

  const handleAcknowledge = async (changeId: number) => {
    try {
      const response = await fetch(
        `/api/schedules/${selectedSchedule?.id}/changes/${changeId}/acknowledge`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Failed to acknowledge');
      // Refresh changes
      handleViewChanges(selectedSchedule!);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge');
    }
  };

  const handleViewSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setActiveTab('preview');
  };

  const getScheduleDisplayName = (schedule: Schedule) => {
    if (schedule.schedule_type === 'custom' && schedule.custom_name) {
      return schedule.custom_name;
    }
    return schedule.schedule_type.charAt(0).toUpperCase() + schedule.schedule_type.slice(1);
  };

  const getStatusBadge = (status: ExtractionStatus, schedule: Schedule) => {
    const config = STATUS_CONFIG[status];
    let message = config.message;
    if (status === 'ready' && schedule.current_version) {
      message = `Ready - ${schedule.current_version.total_rows} rows extracted`;
    } else if (status === 'failed' && schedule.extraction_error) {
      message = `Failed: ${schedule.extraction_error}`;
    }

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 500,
          background: status === 'ready' ? '#e8f5e9' : status === 'failed' ? '#fee' : '#f0f0f0',
          color: config.color,
        }}
      >
        {config.icon} {message}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Filter rows for room view if roomId is provided
  const getFilteredRows = (schedule: Schedule) => {
    if (!schedule.current_version?.extracted_data) return [];
    if (!roomId) return schedule.current_version.extracted_data;

    // This would need the actual room matching data from the API
    // For now, returning all data - the API endpoint `/api/rooms/{id}/schedules` 
    // should return filtered data for room view
    return schedule.current_version.extracted_data;
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading schedules...</div>;
  }

  if (error) {
    return (
      <div
        style={{
          padding: '16px',
          background: '#fee',
          color: '#c00',
          borderRadius: '4px',
          marginBottom: '16px',
        }}
      >
        {String(error)}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #ddd',
          marginBottom: '20px',
        }}
      >
        {(['schedules', 'preview', 'mappings', 'changes'] as TabType[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '3px solid #007bff' : '3px solid transparent',
              color: activeTab === tab ? '#007bff' : '#666',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '14px',
              textTransform: 'capitalize',
            }}
          >
            {tab}
            {tab === 'changes' && selectedSchedule && selectedSchedule.version_count > 1 && (
              <span
                style={{
                  marginLeft: '6px',
                  background: '#ffc107',
                  color: '#333',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '11px',
                }}
              >
                New
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div>
            {schedules.length === 0 ? (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#666',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                }}
              >
                <p>No schedules uploaded yet.</p>
                <p style={{ fontSize: '14px' }}>
                  Upload a schedule PDF to get started.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                  }}
                >
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Version
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Last Updated
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #ddd',
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule) => (
                      <tr key={schedule.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>
                          <span style={{ fontWeight: 500 }}>
                            {getScheduleDisplayName(schedule)}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span
                            style={{
                              textTransform: 'capitalize',
                              color: '#666',
                            }}
                          >
                            {schedule.schedule_type}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {getStatusBadge(schedule.extraction_status, schedule)}
                        </td>
                        <td style={{ padding: '12px' }}>v{schedule.version_count}</td>
                        <td style={{ padding: '12px', color: '#666', fontSize: '13px' }}>
                          {formatDate(schedule.updated_at)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            {schedule.extraction_status === 'ready' && (
                              <>
                                <button
                                  onClick={() => handleViewSchedule(schedule)}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#007bff',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                  }}
                                >
                                  View
                                </button>
                                <button
                                  onClick={() => { setSelectedSchedule(schedule); setActiveTab('mappings'); }}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#6f42c1',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                  }}
                                >
                                  Mappings
                                </button>
                                {schedule.version_count > 1 && (
                                  <button
                                    onClick={() => handleViewChanges(schedule)}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#ffc107',
                                      color: '#333',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                    }}
                                  >
                                    Changes
                                  </button>
                                )}
                              </>
                            )}
                            <button
                              onClick={() => handleExtract(schedule.id)}
                              disabled={schedule.extraction_status === 'processing'}
                              style={{
                                padding: '6px 12px',
                                background: schedule.extraction_status === 'processing' ? '#ccc' : '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: schedule.extraction_status === 'processing' ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              {schedule.extraction_status === 'processing'
                                ? 'Processing...'
                                : 'Re-extract'}
                            </button>
                            <button
                              onClick={() => handleDelete(schedule.id)}
                              style={{
                                padding: '6px 12px',
                                background: '#dc3545',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && selectedSchedule && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>
                {getScheduleDisplayName(selectedSchedule)}
                {selectedSchedule.current_version && (
                  <span style={{ color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                    (v{selectedSchedule.current_version.version_number})
                  </span>
                )}
              </h3>
              <button
                onClick={() => setActiveTab('schedules')}
                style={{
                  padding: '6px 12px',
                  background: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Back to List
              </button>
            </div>

            {selectedSchedule.current_version?.extracted_data ? (
              <div>
                {/* Stats */}
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                  }}
                >
                  <div>
                    <span style={{ color: '#666', fontSize: '13px' }}>Total Rows: </span>
                    <span style={{ fontWeight: 'bold' }}>
                      {selectedSchedule.current_version.total_rows}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#666', fontSize: '13px' }}>Matched: </span>
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                      {selectedSchedule.current_version.matched_count}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#666', fontSize: '13px' }}>Unmatched: </span>
                    <span style={{ fontWeight: 'bold', color: '#ffc107' }}>
                      {selectedSchedule.current_version.unmatched_count}
                    </span>
                  </div>
                </div>

                {/* Room column diagnostic */}
                {selectedSchedule.current_version.unmatched_count > 0 && (() => {
                  const unmatchedRows: any[] = versionDetail?.matched_rooms?.unmatched_rows ?? [];
                  const samples = unmatchedRows
                    .map((r: any) => r.raw_value)
                    .filter(Boolean)
                    .slice(0, 6);
                  return (
                    <div style={{
                      marginBottom: '16px',
                      padding: '12px 16px',
                      background: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: '6px',
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '13px' }}>
                        {selectedSchedule.current_version.unmatched_count} unmatched rows
                        &nbsp;&mdash;&nbsp;column {selectedSchedule.room_column} is reading:&nbsp;
                        <span style={{ fontFamily: 'monospace', fontWeight: 400 }}>
                          {samples.length > 0 ? samples.join(', ') : '(no values)'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '13px' }}>Room column (0-based index):</label>
                        <input
                          type="number"
                          min={0}
                          value={editRoomColumn ?? selectedSchedule.room_column}
                          onChange={e => setEditRoomColumn(Number(e.target.value))}
                          style={{ width: '60px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
                        />
                        <button
                          onClick={handleSaveRoomColumn}
                          disabled={savingColumn || editRoomColumn === selectedSchedule.room_column}
                          style={{
                            padding: '4px 12px',
                            background: savingColumn || editRoomColumn === selectedSchedule.room_column ? '#ccc' : '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: savingColumn || editRoomColumn === selectedSchedule.room_column ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          {savingColumn ? 'Re-extracting...' : 'Save & Re-extract'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Data Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        {(() => {
                          const rows = getFilteredRows(selectedSchedule);
                          if (rows.length === 0) return null;
                          const columns = Object.keys(rows[0]).filter(k => k !== '_row_index');
                          return columns.map((key) => (
                            <th
                              key={key}
                              style={{
                                padding: '10px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                borderBottom: '2px solid #ddd',
                                borderRight: '1px solid #ddd',
                              }}
                            >
                              {key}
                            </th>
                          ));
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredRows(selectedSchedule).map((row, index) => {
                        const columns = Object.keys(row).filter(k => k !== '_row_index');
                        return (
                          <tr
                            key={index}
                            style={{
                              borderBottom: '1px solid #eee',
                              background: index % 2 === 0 ? '#fff' : '#fafafa',
                            }}
                          >
                            {columns.map((key) => (
                              <td
                                key={key}
                                style={{
                                  padding: '8px 10px',
                                  borderRight: '1px solid #eee',
                                }}
                              >
                                {String(row[key] ?? '')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                <p>No data available yet.</p>
                {selectedSchedule.extraction_status === 'pending' && (
                  <p>Extraction is pending...</p>
                )}
                {selectedSchedule.extraction_status === 'failed' && (
                  <p style={{ color: '#dc3545' }}>
                    Extraction failed: {selectedSchedule.extraction_error}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mappings Tab */}
        {activeTab === 'mappings' && (
          selectedSchedule ? (
            <ScheduleRoomMapping
              schedule={selectedSchedule}
              token={token}
              onMappingsSaved={() => {
                loadSchedules();
              }}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '8px' }}>
              <p>Select a schedule to view room mappings.</p>
              <p style={{ fontSize: '14px' }}>Click "Mappings" on a ready schedule in the Schedules tab.</p>
            </div>
          )
        )}

        {/* Changes Tab */}
        {activeTab === 'changes' && (
          <div>
            {loadingChanges ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>Loading changes...</div>
            ) : changesSummary ? (
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <h3 style={{ margin: 0 }}>
                    Changes Summary
                    <span style={{ color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                      (v{changesSummary.version_from} → v{changesSummary.version_to})
                    </span>
                  </h3>
                  <button
                    onClick={() => setActiveTab('schedules')}
                    style={{
                      padding: '6px 12px',
                      background: '#f0f0f0',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Back to List
                  </button>
                </div>

                {/* Summary Cards */}
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#e8f5e9',
                      borderRadius: '4px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                      {changesSummary.added}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Added</div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#fff3e0',
                      borderRadius: '4px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                      {changesSummary.modified}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Modified</div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#fee',
                      borderRadius: '4px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                      {changesSummary.removed}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Removed</div>
                  </div>
                </div>

                {/* Changes List */}
                {changesSummary.changes.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {changesSummary.changes.map((change) => (
                      <div
                        key={change.id}
                        style={{
                          padding: '16px',
                          background: change.is_acknowledged ? '#f8f9fa' : '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          opacity: change.is_acknowledged ? 0.7 : 1,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background:
                                  change.change_type === 'added'
                                    ? '#e8f5e9'
                                    : change.change_type === 'modified'
                                    ? '#fff3e0'
                                    : '#fee',
                                color:
                                  change.change_type === 'added'
                                    ? '#28a745'
                                    : change.change_type === 'modified'
                                    ? '#f57c00'
                                    : '#dc3545',
                              }}
                            >
                              {change.change_type.charAt(0).toUpperCase() +
                                change.change_type.slice(1)}
                            </span>
                            <span style={{ fontWeight: 500 }}>Room {change.room_number}</span>
                          </div>
                          {!change.is_acknowledged && (
                            <button
                              onClick={() => handleAcknowledge(change.id)}
                              style={{
                                padding: '6px 12px',
                                background: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Acknowledge
                            </button>
                          )}
                          {change.is_acknowledged && (
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                background: '#e0e0e0',
                                color: '#666',
                              }}
                            >
                              ✓ Acknowledged
                            </span>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
                          {change.change_summary}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#666',
                    }}
                  >
                    No changes found between versions.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <p>No changes to display.</p>
                <p style={{ fontSize: '14px' }}>
                  Select a schedule with multiple versions to view changes.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {deleteModalSchedule && (
        <DeleteConfirmationModal
          schedule={deleteModalSchedule}
          onDeleteAll={handleDeleteAllVersions}
          onDeleteLatest={handleDeleteLatestVersion}
          onCancel={() => setDeleteModalSchedule(null)}
        />
      )}
    </div>
  );
}
